/* Name Links Review — every saved slip name link with a health level and one-click remove. */
import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API, getAuthHeader } from "@/App";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Link2, Loader2, Search, Trash2 } from "lucide-react";

const LV = {
  red: { label: "Looks wrong", cls: "bg-red-100 text-red-700 border-red-300" },
  amber: { label: "Check", cls: "bg-amber-100 text-amber-800 border-amber-300" },
  green: { label: "OK", cls: "bg-emerald-100 text-emerald-700 border-emerald-300" },
};

export const NameLinksReviewButton = () => {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");
  const [warnOnly, setWarnOnly] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setBusy(true);
    try {
      const r = await axios.get(`${API}/packing-list/name-aliases/review`, { headers: getAuthHeader() });
      setData(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not load name links"); }
    finally { setBusy(false); }
  };
  const remove = async (ids, label) => {
    if (!window.confirm(`Remove ${label}? Future slip checks will no longer use ${ids.length > 1 ? "these links" : "this link"}.`)) return;
    try {
      const r = await axios.post(`${API}/packing-list/name-aliases/bulk-delete`, { ids }, { headers: getAuthHeader() });
      toast.success(`${r.data.deleted} link(s) removed`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not remove"); }
  };

  const rows = (data?.rows || []).filter(r => (!warnOnly || r.level !== "green") &&
    (!q || `${r.file_name} ${r.slip_name} ${r.by_name}`.toUpperCase().includes(q.toUpperCase())));
  const reds = (data?.rows || []).filter(r => r.level === "red");

  return (
    <>
      <button type="button" onClick={() => { setOpen(true); load(); }} data-testid="name-links-review-btn"
              className="h-7 px-2.5 rounded-full border border-sky-300 bg-sky-50 text-sky-700 text-[11px] font-semibold hover:bg-sky-100 inline-flex items-center gap-1">
        <Link2 className="w-3 h-3" />Name Links
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-[95vw] w-[1000px] max-h-[88vh] overflow-y-auto" data-testid="name-links-dialog">
          <DialogHeader><DialogTitle>Name Links Review</DialogTitle></DialogHeader>
          <p className="text-xs text-slate-500 -mt-2">Saved links "register name → slip name" used by every packing-slip check. Remove links that join different products.</p>
          {busy && !data ? <div className="py-10 text-center"><Loader2 className="w-5 h-5 animate-spin inline" /></div> : data && (<>
            <div className="flex flex-wrap items-center gap-2" data-testid="name-links-counts">
              {Object.entries(LV).map(([k, m]) => (
                <span key={k} className={`text-xs font-semibold border rounded-full px-2 py-0.5 ${m.cls}`}>{m.label}: {data.counts[k] || 0}</span>))}
              <div className="relative ml-auto">
                <Search className="w-3.5 h-3.5 absolute left-2 top-2 text-slate-400" />
                <input value={q} onChange={e => setQ(e.target.value)} placeholder="Search names…"
                       className="h-8 pl-7 pr-2 text-xs border rounded-md w-48" data-testid="name-links-search" />
              </div>
              <label className="text-xs inline-flex items-center gap-1 cursor-pointer">
                <input type="checkbox" checked={warnOnly} onChange={e => setWarnOnly(e.target.checked)} data-testid="name-links-warn-only" />Warnings only
              </label>
              {reds.length > 0 && (
                <button type="button" onClick={() => remove(reds.map(r => r.id), `all ${reds.length} "Looks wrong" link(s)`)}
                        className="h-8 px-2.5 rounded-md bg-red-600 text-white text-xs font-semibold hover:bg-red-700" data-testid="name-links-remove-red">
                  Remove all red ({reds.length})
                </button>)}
            </div>
            <div className="overflow-x-auto border rounded-md mt-2">
              <table className="w-full text-xs" data-testid="name-links-table">
                <thead className="bg-slate-50"><tr className="text-left">
                  {["Level", "Register name", "", "Slip name", "Why", "Saved by", "When", ""].map((h, i) => <th key={i} className="px-2 py-1.5">{h}</th>)}
                </tr></thead>
                <tbody>
                  {rows.map(r => (
                    <tr key={r.id} className={`border-t ${r.level === "red" ? "bg-red-50/50" : ""}`} data-testid={`name-link-row-${r.id}`}>
                      <td className="px-2 py-1.5"><span className={`text-[10px] font-bold border rounded-full px-1.5 ${LV[r.level].cls}`}>{LV[r.level].label}</span></td>
                      <td className="px-2 py-1.5 font-medium">{r.file_name || r.file_key}</td>
                      <td className="px-1 text-slate-400">→</td>
                      <td className="px-2 py-1.5 font-medium">{r.slip_name || r.slip_key}</td>
                      <td className="px-2 py-1.5 text-slate-500">{r.why}</td>
                      <td className="px-2 py-1.5">{r.by_name || "—"}</td>
                      <td className="px-2 py-1.5 whitespace-nowrap">{r.created_at ? new Date(r.created_at).toLocaleDateString() : "—"}</td>
                      <td className="px-2 py-1.5">
                        <button type="button" onClick={() => remove([r.id], `the link "${r.file_name}" → "${r.slip_name}"`)}
                                className="text-red-600 hover:text-red-800" title="Remove link" data-testid={`name-link-remove-${r.id}`}>
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </td>
                    </tr>))}
                  {!rows.length && <tr><td colSpan={8} className="text-center text-slate-400 py-6">No name links</td></tr>}
                </tbody>
              </table>
            </div>
          </>)}
        </DialogContent>
      </Dialog>
    </>
  );
};
